import { CheckIcon } from "@phosphor-icons/react";
import { useOne } from "@refinedev/core";
import { Card, Flex, Typography } from "antd";
import { Spinner, radius } from "@libertify/ui";
import { AudioSample } from "pages/media/components/AudioSample";
import { DeleteButton } from "components/DeleteButton";
import { useAntTheme } from "hooks/useAntTheme";
import { ASSET_STALE_TIME } from "pages/media/constants";
import { MediaAsset } from "pages/media/types";

export type MusicAssetCardProps = {
  asset: Partial<MediaAsset>;
  isBrandKit: boolean;
  isSelected: boolean;
  onSelect: () => void;
  volume?: number;
  assetLibraryId?: string;
  onDeleted?: (assetId: string) => void;
};

export const MusicAssetCard = ({
  asset,
  isBrandKit,
  isSelected,
  onSelect,
  volume,
  assetLibraryId,
  onDeleted,
}: MusicAssetCardProps) => {
  const { theme } = useAntTheme();

  const { data: assetData } = useOne<MediaAsset>({
    resource: `media/media/assets`,
    id: asset.id,
    queryOptions: {
      enabled: Boolean(!asset.path && asset.id),
      refetchInterval(data) {
        return data?.data.path ? false : 3000;
      },
      staleTime: ASSET_STALE_TIME,
    },
    errorNotification() {
      return false;
    },
  });

  const path = assetData?.data.path ?? asset.path;

  return (
    <Card
      hoverable
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest(".ant-btn") || target.closest(".ant-slider")) {
          return;
        }
        onSelect();
      }}
      style={{
        width: "100%",
        borderRadius: radius.box,
        boxShadow: `0 0 10px ${theme.colorBorder}`,
        cursor: "pointer",
      }}
      styles={{
        body: {
          padding: 16,
          position: "relative",
        },
      }}
    >
      {isSelected && (
        <Flex
          align="center"
          justify="center"
          style={{
            position: "absolute",
            backgroundColor: theme.colorPrimary,
            width: 32,
            height: 32,
            border: `4px solid ${theme.colorBgElevated}`,
            borderRadius: radius.circle,
            color: theme.colorBgElevated,
            right: -8,
            top: -8,
            zIndex: 2,
          }}
        >
          <CheckIcon size={18} />
        </Flex>
      )}
      <Flex vertical gap={8}>
        <Flex align="center" justify="space-between" gap={8}>
          <Flex align="center" gap={8} style={{ minWidth: 0 }}>
            <Typography.Text ellipsis style={{ minWidth: 0 }}>
              {asset.description}
            </Typography.Text>
          </Flex>
          {!isBrandKit && asset.id && assetLibraryId && (
            <DeleteButton
              recordItemId={asset.id}
              resource={`media/asset_libraries/${assetLibraryId}/assets`}
              onSuccess={() => onDeleted?.(asset.id as string)}
            />
          )}
        </Flex>
        {path ? (
          <AudioSample audio={path} volume={volume} canDelete={false} />
        ) : (
          <Spinner size="sm" data-testid="music-asset-spinner" />
        )}
      </Flex>
    </Card>
  );
};
